import Box from '@mui/material/Box';
import React, {memo} from 'react';
import {colors, gradients, overlay} from '@/styles/tokens';

// Icons are flat monochrome images, so they are recoloured with a filter chain rather than a fill/mask.
// `brightness(0) saturate(100%)` flattens the source to black, and the rest tints it to colors.primary.deep.
const iconBlueFilter =
    'brightness(0) saturate(100%) invert(43.5%) sepia(99.3%) saturate(486.3%) hue-rotate(185deg) brightness(48%) contrast(106.2%)';

/**
 * Opt-in navy fill on hover (matches the KeyServices cards): the card turns dark, copy flips to white,
 * the icon tile inverts to white with a blue icon, and the inner white ring goes transparent so the
 * fill covers the whole card.
 */
const blueHoverSx = {
    backgroundImage: gradients.navyCardDeep,
    '& > *': {borderColor: 'transparent !important'},
    // `!important` is required, not defensive: link classes in globals.scss (`.card-redirection-heading`,
    // `.menu-links`) set their colour with `!important`, so a plain declaration here loses and the
    // heading stays black on the navy fill. Both being `!important`, this wins on specificity.
    '& .font-headers, & .font-headers a, & .font-regular-text, & .font-regular-text a': {
        color: `${colors.text.inverse} !important`
    },
    '& .common-icon-tile, & .common-icon-tile-inner': {
        background: colors.background.default
    },
    '& .home-service-icon': {
        filter: `${iconBlueFilter} !important`
    },
    // The pale "best suited for" footer would leave white text on a light tint.
    '& .card-tinted-footer': {
        bgcolor: overlay.whiteTenth
    }
} as const;

const CommonCard = (props: any) => {
    // `padding`: inner padding in theme spacing units (8px each). Defaults to 3 (24px).
    const {children, blueHover, padding = 3} = props;
    return (
        <Box
            height="100%"
            borderRadius={7}
            sx={{
                bgcolor: colors.background.default,
                border: '1px solid #e5e7eb',
                transition: 'transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease, background-color 0.25s ease',
                backgroundImage: 'none',
                '&:hover': {
                    borderColor: colors.primary.deep,
                    transform: 'translateY(-6px)',
                    boxShadow: '0 12px 32px rgba(10, 1, 99, 0.16)',
                    ...(blueHover ? blueHoverSx : {})
                }
            }}
        >
            <Box
                border={3}
                sx={{borderColor: `${colors.background.default} !important`, height: '100%', display: 'flex', flexDirection: 'column'}}
                p={padding}
                borderRadius={7}
            >
                {children}
            </Box>
        </Box>
    );
};

export default memo(CommonCard);
